import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import "@/studio/studio.css";

import { useStudioScroll, scrollToId } from "@/studio/lib/useStudioScroll";
import { fetchWork } from "@/studio/lib/projects";
import Cursor from "@/studio/components/Cursor";
import UIMock from "@/studio/components/UIMock";
import BlackHole from "@/studio/components/BlackHole";

const ease = [0.16, 1, 0.3, 1];

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function StudioProject() {
  const { slug } = useParams();
  useStudioScroll();
  const [work, setWork] = useState(null); // null = still loading

  useEffect(() => {
    let alive = true;
    fetchWork().then((items) => {
      if (alive) setWork(items);
    });
    return () => {
      alive = false;
    };
  }, []);

  const p = work ? work.find((w) => w.slug === slug) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
    if (p) document.title = `${p.name} — Parabolum Studio`;
  }, [p]);

  if (work === null) {
    return (
      <div className="studio-root flex min-h-screen items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-[var(--line)] border-t-[var(--ink)]" />
      </div>
    );
  }

  if (!p) return <Navigate to="/" replace />;

  const idx = work.findIndex((w) => w.slug === slug);
  const next = work[(idx + 1) % work.length];

  // Uploaded images take priority over the animated UIMock placeholders.
  const heroImg = p.cover || (p.gallery && p.gallery[0]) || "";
  const galleryImgs = p.cover ? p.gallery || [] : (p.gallery || []).slice(1);

  return (
    <div className="studio-root relative min-h-screen">
      <Cursor />

      {/* top bar */}
      <header className="fixed inset-x-0 top-0 z-[120]" style={{ mixBlendMode: "difference" }}>
        <div className="mx-auto flex max-w-[1500px] items-center justify-between px-5 py-5 md:px-10 md:py-6">
          <Link to="/" data-cursor="big" className="ff-display text-[19px] font-extrabold tracking-tight text-white">
            PARABOLUM<span className="text-[#E4FF00]">.</span>
          </Link>
          <Link
            to="/"
            data-cursor="big"
            className="flex items-center gap-2 text-[13px] font-medium text-white"
          >
            <span>←</span> Back to studio
          </Link>
        </div>
      </header>

      {/* hero */}
      <section className="relative overflow-hidden px-5 pb-16 pt-32 md:px-10 md:pb-24 md:pt-40">
        <div className="mx-auto max-w-[1500px]">
          <Reveal>
            <div className="flex flex-wrap items-center gap-3">
              <span className="chip-lime">{p.category}</span>
              <span className="label text-[var(--ink-faint)]">{p.year}</span>
              <span className="label text-[var(--ink-faint)]">Case {p.index} / {p.total || "04"}</span>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="ff-display mt-8 text-6xl font-extrabold leading-[0.88] tracking-tight md:text-[10rem]">
              {p.name}
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="ff-serif mt-6 max-w-2xl text-2xl text-[var(--ink-soft)] md:text-4xl">
              {p.tagline}
            </p>
          </Reveal>
        </div>
      </section>

      {/* primary visual */}
      <section className="px-5 md:px-10">
        <div className="mx-auto max-w-[1500px]">
          <Reveal>
            <div className="relative overflow-hidden rounded-[32px] bg-[#0a0a0a] p-8 shadow-premium md:p-16">
              <div
                className="pointer-events-none absolute inset-0 opacity-60"
                style={{
                  background:
                    "radial-gradient(100% 80% at 70% 0%, rgba(228,255,0,0.12), transparent 55%)",
                }}
              />
              <div className="relative mx-auto max-w-2xl">
                {heroImg ? (
                  <img
                    src={heroImg}
                    alt={p.name}
                    className="w-full rounded-2xl object-cover shadow-soft"
                  />
                ) : (
                  <UIMock kind={p.screens[0]} />
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* meta + narrative */}
      <section className="px-5 py-20 md:px-10 md:py-28">
        <div className="mx-auto grid max-w-[1500px] grid-cols-1 gap-12 lg:grid-cols-12">
          {/* sticky meta */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-8">
              <MetaBlock title="Services">
                <div className="flex flex-wrap gap-2">
                  {p.services.map((s) => (
                    <span key={s} className="rounded-full border border-[var(--line)] px-3 py-1 text-[13px] text-[var(--ink-soft)]">
                      {s}
                    </span>
                  ))}
                </div>
              </MetaBlock>
              <MetaBlock title="Tech stack">
                <div className="flex flex-wrap gap-2">
                  {p.stack.map((s) => (
                    <span key={s} className="rounded-full border border-[var(--line)] px-3 py-1 text-[13px] text-[var(--ink-soft)]">
                      {s}
                    </span>
                  ))}
                </div>
              </MetaBlock>
            </div>
          </div>

          {/* narrative */}
          <div className="lg:col-span-8">
            <Reveal>
              <Block label="The challenge" body={p.challenge} />
            </Reveal>
            <Reveal>
              <Block label="The solution" body={p.solution} />
            </Reveal>

            <Reveal>
              <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden rounded-[28px] border border-[var(--line)] bg-[var(--line)] sm:grid-cols-3">
                {p.outcome.map((o) => (
                  <div key={o.k} className="bg-[var(--paper)] p-7">
                    <div className="ff-display text-4xl font-extrabold tracking-tight md:text-5xl">
                      {o.v}
                    </div>
                    <div className="mt-2 text-[13px] text-[var(--ink-soft)]">{o.k}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* gallery */}
      <section className="px-5 pb-8 md:px-10">
        <div className="mx-auto max-w-[1500px] space-y-8">
          {galleryImgs.length > 0
            ? galleryImgs.map((src, i) => (
                <Reveal key={`img-${i}`}>
                  <div className="relative overflow-hidden rounded-[32px] bg-[#0a0a0a] p-3 shadow-soft md:p-4">
                    <img
                      src={src}
                      alt={`${p.name} — ${i + 1}`}
                      loading="lazy"
                      className="w-full rounded-[24px] object-cover"
                    />
                  </div>
                </Reveal>
              ))
            : p.screens.slice(1).map((s, i) => (
                <Reveal key={s + i}>
                  <div className="relative overflow-hidden rounded-[32px] bg-[#0a0a0a] p-8 shadow-soft md:p-14">
                    <div className="relative mx-auto max-w-2xl">
                      <UIMock kind={s} />
                    </div>
                  </div>
                </Reveal>
              ))}
        </div>
      </section>

      {/* interested CTA — bridge to contact */}
      <section className="relative bg-[var(--paper)] px-5 py-24 md:px-10 md:py-32">
        <div className="mx-auto max-w-[1500px]">
          <Reveal>
            <div className="grid items-end gap-10 md:grid-cols-12">
              <div className="md:col-span-7">
                <span className="label text-[var(--ink-faint)]">
                  Something similar?
                </span>
                <h2 className="ff-display mt-4 text-4xl font-extrabold leading-[1] tracking-tight text-[var(--ink)] md:text-6xl">
                  Interested in building
                  <br />
                  something like this?
                </h2>
              </div>
              <div className="md:col-span-5">
                <a
                  href="mailto:hello@parabolum.digital"
                  data-testid="work-cta-email"
                  data-cursor="big"
                  className="group inline-flex items-center gap-3 rounded-full border border-[var(--line)] bg-white/[0.02] px-7 py-4 ff-body text-[14px] tracking-tight text-[var(--ink)] transition-all duration-500 hover:border-[var(--ink)]/30 hover:bg-white"
                >
                  hello@parabolum.digital
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--ink)] text-[var(--paper)] transition-transform duration-500 group-hover:translate-x-1">
                    →
                  </span>
                </a>
                <p className="mt-4 text-[13px] text-[var(--ink-soft)]">
                  Direct line to the founder. Reply within 24 hours.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* next project */}
      <section className="relative overflow-hidden bg-[#0a0a0a] px-5 py-24 text-white md:px-10 md:py-32">
        <div className="pointer-events-none absolute inset-y-0 right-[-4%] z-0 hidden w-[44%] items-center md:flex">
          <BlackHole className="h-[60vh] w-full" aura={false} glow parallax={16} />
        </div>
        <div className="relative z-10 mx-auto max-w-[1500px]">
          <span className="label text-white/40">Next case</span>
          <Link to={`/work/${next.slug}`} data-cursor="big" className="group mt-6 block">
            <h2 className="ff-display text-6xl font-extrabold tracking-tight transition-colors group-hover:text-[#E4FF00] md:text-9xl">
              {next.name}
            </h2>
            <div className="mt-6 inline-flex items-center gap-3 text-[15px] font-semibold">
              View case study
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E4FF00] text-black transition-transform duration-500 group-hover:translate-x-1">
                →
              </span>
            </div>
          </Link>

          <div className="mt-20 border-t border-white/10 pt-8">
            <button
              onClick={() => scrollToId("top")}
              className="text-[13px] text-white/40 hover:text-white"
            >
              Back to top
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function MetaBlock({ title, children }) {
  return (
    <div>
      <div className="label mb-3 text-[var(--ink-faint)]">{title}</div>
      {children}
    </div>
  );
}

function Block({ label, body }) {
  return (
    <div className="mb-12">
      <div className="label text-[var(--ink-faint)]">{label}</div>
      <p className="mt-4 text-2xl leading-[1.4] tracking-tight text-[var(--ink)] md:text-[32px]">
        {body}
      </p>
    </div>
  );
}
